import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast, Toaster } from "sonner";
import { CreditCard, Building2, Shield, Trash2, Check, AlertTriangle, Plus } from "lucide-react";

export default function PaymentMethods() {
  const { user } = useAuth();
  const [methods, setMethods] = useState([]);
  const [tab, setTab] = useState("card");
  const [loading, setLoading] = useState(false);
  const [nmi, setNmi] = useState({ card_last4: "", card_brand: "visa", exp_month: "", exp_year: "", nickname: "" });

  const load = useCallback(async () => {
    const { data } = await api.get("/billing/methods");
    setMethods(data);
  }, []);

  const pollSetup = useCallback(async (sid, attempts = 0) => {
    if (attempts >= 8) return;
    try {
      const { data } = await api.get(`/billing/methods/setup/${sid}`);
      if (data.saved) {
        toast.success("Payment method saved");
        window.history.replaceState({}, "", "/dashboard/billing/methods");
        load();
        return;
      }
      setTimeout(() => pollSetup(sid, attempts + 1), 2000);
    } catch (e) {
      setTimeout(() => pollSetup(sid, attempts + 1), 2000);
    }
  }, [load]);

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    const status = params.get("setup");
    if (sid && status === "success") pollSetup(sid);
    if (status === "cancelled") toast.message("Setup cancelled");
  }, [load, pollSetup]);

  const addStripe = async (kind) => {
    if (!isApproved()) { toast.error("Account must be approved first"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/billing/methods/setup", { kind, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Setup failed");
      setLoading(false);
    }
  };

  const addNmi = async (e) => {
    e.preventDefault();
    if (!isApproved()) { toast.error("Account must be approved first"); return; }
    if (!nmi.card_last4 || nmi.card_last4.length < 4) { toast.error("Card last 4 required"); return; }
    try {
      await api.post("/billing/methods/nmi", {
        ...nmi,
        exp_month: nmi.exp_month ? parseInt(nmi.exp_month) : null,
        exp_year: nmi.exp_year ? parseInt(nmi.exp_year) : null,
      });
      toast.success("NMI method stored");
      setNmi({ card_last4: "", card_brand: "visa", exp_month: "", exp_year: "", nickname: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setDefault = async (id) => {
    try { await api.put(`/billing/methods/${id}/default`); toast.success("Default set"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this payment method?")) return;
    try { await api.delete(`/billing/methods/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const isApproved = () => !user?.approval_status || user?.approval_status === "approved";

  return (
    <DashboardLayout>
      <Toaster position="top-right" richColors />
      <div className="mb-6 sm:mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Billing</div>
        <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter">Payment methods</h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          Attach a credit card or bank account. Methods can auto-charge your wallet so shipments, Smart Services, and rebills always have funds to settle against.
        </p>
      </div>

      {!isApproved() && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-sm p-4 flex items-start gap-2" data-testid="methods-pending-notice">
          <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-zinc-700">Your account is pending approval. Payment methods can be attached once approved.</div>
        </div>
      )}

      {methods.some((m) => m.simulated) && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-sm p-4 flex items-start gap-2" data-testid="simulated-notice">
          <Shield size={16} className="brand-text mt-0.5 shrink-0" />
          <div className="text-sm text-zinc-700">
            <b>Demo mode:</b> Payment methods below are simulated. Add a real Stripe secret key (<span className="font-mono-plex text-xs">STRIPE_API_KEY=sk_test_...</span>) to <span className="font-mono-plex text-xs">backend/.env</span> and new methods will tokenize against live Stripe.
          </div>
        </div>
      )}

      {/* Gateway tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {[
          { code: "card", label: "Credit Card", icon: CreditCard, sub: "via Stripe" },
          { code: "bank", label: "US Bank (ACH)", icon: Building2, sub: "via Stripe" },
          { code: "nmi",  label: "NMI Gateway", icon: Shield, sub: "Card / bank via NMI" },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.code} data-testid={`methods-tab-${t.code}`} onClick={() => setTab(t.code)}
              className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm text-sm border transition-all ${tab === t.code ? "border-[var(--brand-accent)] bg-blue-50/60 brand-text" : "border-zinc-200 text-zinc-700 hover:bg-zinc-50"}`}>
              <Icon size={14} />
              <span>{t.label}</span>
              <span className="hidden sm:inline text-xs text-zinc-400">· {t.sub}</span>
            </button>
          );
        })}
      </div>

      {/* Add sections */}
      {tab === "card" && (
        <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-8" data-testid="add-card-panel">
          <div className="font-display text-lg font-bold mb-2">Add credit card</div>
          <p className="text-sm text-zinc-600 mb-4">You'll be redirected to Stripe's secure checkout to enter card details. Your card is stored in Stripe's vault — SellePack only sees last-4 and brand.</p>
          <button data-testid="add-card-btn" onClick={() => addStripe("card")} disabled={loading || !isApproved()}
            className="brand-bg text-white px-5 py-2 rounded-sm text-sm disabled:opacity-50 flex items-center gap-2">
            <Plus size={14} /> {loading ? "Redirecting…" : "Add card via Stripe"}
          </button>
        </div>
      )}
      {tab === "bank" && (
        <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-8" data-testid="add-bank-panel">
          <div className="font-display text-lg font-bold mb-2">Add US bank account (ACH)</div>
          <p className="text-sm text-zinc-600 mb-4">You'll be redirected to Stripe to connect your US bank via instant verification. ACH debits are ideal for larger wallet top-ups and carry lower processing fees.</p>
          <button data-testid="add-bank-btn" onClick={() => addStripe("bank")} disabled={loading || !isApproved()}
            className="brand-bg text-white px-5 py-2 rounded-sm text-sm disabled:opacity-50 flex items-center gap-2">
            <Plus size={14} /> {loading ? "Redirecting…" : "Connect bank via Stripe"}
          </button>
        </div>
      )}
      {tab === "nmi" && (
        <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-8" data-testid="add-nmi-panel">
          <div className="font-display text-lg font-bold mb-2">Add card via NMI gateway</div>
          <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 mb-4 text-xs text-zinc-600">
            <b>Pending NMI credentials.</b> Enter card details to register a placeholder now — we'll activate it automatically when your NMI Gateway ID and Security Key are configured in <span className="font-mono-plex">backend/.env</span>.
          </div>
          <form onSubmit={addNmi} className="grid md:grid-cols-5 gap-3 items-end" data-testid="nmi-form">
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Last 4</label>
              <input data-testid="nmi-last4" required maxLength={4} value={nmi.card_last4}
                onChange={(e) => setNmi({ ...nmi, card_last4: e.target.value.replace(/\D/g, '') })}
                disabled={!isApproved()}
                className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm font-mono-plex disabled:bg-zinc-50" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Brand</label>
              <select data-testid="nmi-brand" value={nmi.card_brand}
                onChange={(e) => setNmi({ ...nmi, card_brand: e.target.value })}
                disabled={!isApproved()}
                className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm disabled:bg-zinc-50">
                {["visa","mastercard","amex","discover"].map((b) => <option key={b} value={b}>{b.toUpperCase()}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Exp MM</label>
              <input data-testid="nmi-exp-month" type="number" min="1" max="12" value={nmi.exp_month}
                onChange={(e) => setNmi({ ...nmi, exp_month: e.target.value })} disabled={!isApproved()}
                className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm disabled:bg-zinc-50" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Exp YYYY</label>
              <input data-testid="nmi-exp-year" type="number" min="2025" max="2050" value={nmi.exp_year}
                onChange={(e) => setNmi({ ...nmi, exp_year: e.target.value })} disabled={!isApproved()}
                className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm disabled:bg-zinc-50" />
            </div>
            <button data-testid="add-nmi-btn" type="submit" disabled={!isApproved()}
              className="brand-bg text-white px-4 py-2 rounded-sm text-sm disabled:opacity-50">Save</button>
          </form>
        </div>
      )}

      {/* List */}
      <div className="bg-white border border-zinc-200 rounded-sm p-5 sm:p-6">
        <div className="font-display text-xl font-bold mb-4">Saved methods</div>
        {methods.length === 0 ? (
          <div className="py-10 text-center text-zinc-500 text-sm">No methods yet. Add a card or bank above.</div>
        ) : (
          <div className="space-y-2">
            {methods.map((m) => (
              <div key={m.method_id} data-testid={`method-${m.method_id}`}
                className={`border rounded-sm p-4 flex items-center justify-between flex-wrap gap-3 ${m.is_default ? "border-[var(--brand-accent)] bg-blue-50/30" : "border-zinc-200"}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm brand-bg text-white flex items-center justify-center">
                    {m.type === "bank" ? <Building2 size={18} /> : <CreditCard size={18} />}
                  </div>
                  <div>
                    <div className="font-display font-bold text-zinc-900">
                      {(m.brand || m.type || "").toUpperCase()} ···· {m.last4}
                      {m.is_default && <span className="ml-2 text-[10px] tracking-[0.2em] brand-text font-bold">DEFAULT</span>}
                    </div>
                    <div className="text-xs text-zinc-500 flex items-center gap-2">
                      <span className="uppercase">{m.gateway}</span>
                      {m.exp_month && m.exp_year && <span>· exp {String(m.exp_month).padStart(2,'0')}/{m.exp_year}</span>}
                      {m.status === "pending_nmi_keys" && <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded-sm uppercase tracking-wider">Pending NMI keys</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {!m.is_default && (
                    <button data-testid={`set-default-${m.method_id}`} onClick={() => setDefault(m.method_id)}
                      className="text-xs border border-zinc-300 px-3 py-1 rounded-sm hover:bg-zinc-50 flex items-center gap-1">
                      <Check size={12} /> Make default
                    </button>
                  )}
                  <button data-testid={`delete-${m.method_id}`} onClick={() => del(m.method_id)}
                    className="text-xs border border-red-300 text-red-700 px-3 py-1 rounded-sm hover:bg-red-50 flex items-center gap-1">
                    <Trash2 size={12} /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
